import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ArrowLeft, Calendar, CheckCircle2, Clock3, Layers3, Lock, Sparkles, Target } from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import {
  flattenProductRoadmapDays,
  withProductRoadmapDates,
} from "../utils/buildProductRoadmap";

const STATUSES = ["TODO", "IN_PROGRESS", "DONE"];

export default function ProductTasksBoardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dayId = searchParams.get("dayId");

  const [loading, setLoading] = useState(true);
  const [hasProAccess, setHasProAccess] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [currentDay, setCurrentDay] = useState(null);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!user?.uid || !dayId) return;

    const load = async () => {
      try {
        setLoading(true);

        const [userSnap, roadmapSnap] = await Promise.all([
          getDoc(doc(db, "users", user.uid)),
          getDoc(doc(db, "product_roadmaps", user.uid)),
        ]);

        const hasPro = userSnap.exists() && userSnap.data()?.plan === "PRO";
        setHasProAccess(hasPro);

        if (!roadmapSnap.exists()) {
          setRoadmap(null);
          return;
        }

        const roadmapData = roadmapSnap.data();
        setRoadmap(roadmapData);

        const flat = flattenProductRoadmapDays(roadmapData.roadmapRef);
        const dated = withProductRoadmapDates(
          flat,
          roadmapData.consistency?.startedAt,
          roadmapData.progress?.completedDays || {}
        );
        const selectedDay = dated.find((item) => item.id === dayId) || null;
        setCurrentDay(selectedDay);

        const generatedTasks = (selectedDay?.checklist?.length
          ? selectedDay.checklist
          : selectedDay?.howTo || []
        ).map((title, index) => ({
          id: `${dayId}_task_${index}`,
          title,
          status: "TODO",
          sequence: index + 1,
        }));

        const progressSnap = await getDoc(
          doc(db, "users", user.uid, "productRoadmapDays", dayId)
        );
        const saved = progressSnap.exists() ? progressSnap.data()?.tasks || {} : {};

        setTasks(
          generatedTasks.map((task) => ({
            ...task,
            status: saved[task.id]?.status || "TODO",
          }))
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.uid, dayId]);

  const isLocked = useMemo(() => {
    if (!currentDay) return false;
    return currentDay.status === "upcoming";
  }, [currentDay]);

  const grouped = useMemo(
    () =>
      STATUSES.reduce((acc, status) => {
        acc[status] = tasks.filter((task) => task.status === status);
        return acc;
      }, {}),
    [tasks]
  );

  const allTasksCompleted = tasks.length > 0 && tasks.every((task) => task.status === "DONE");
  const doneCount = tasks.filter((task) => task.status === "DONE").length;
  const inProgressCount = tasks.filter((task) => task.status === "IN_PROGRESS").length;
  const todoCount = tasks.filter((task) => task.status === "TODO").length;
  const completionPercent = tasks.length
    ? Math.round((doneCount / tasks.length) * 100)
    : 0;
  const isDayCompleted = !!currentDay?.completed;
  const isInteractionLocked = isLocked || isDayCompleted || !hasProAccess;
  const boardStatusLabel = isDayCompleted
    ? "Completed"
    : isLocked
    ? "Locked until this build day arrives"
    : !hasProAccess
    ? "PRO required to manage this board"
    : allTasksCompleted
    ? "Ready to mark complete"
    : "Active and ready to execute";

  const persistTask = async (task, status) => {
    await setDoc(
      doc(db, "users", user.uid, "productRoadmapDays", dayId),
      {
        tasks: {
          [task.id]: {
            title: task.title,
            status,
            updatedAt: serverTimestamp(),
          },
        },
      },
      { merge: true }
    );
  };

  const updateStatus = async (id, status) => {
    if (isInteractionLocked) return;
    const task = tasks.find((item) => item.id === id);
    if (!task) return;

    setTasks((prev) => prev.map((item) => (item.id === id ? { ...item, status } : item)));
    await persistTask(task, status);
  };

  const onDragEnd = async (result) => {
    if (!result.destination || isInteractionLocked) return;
    const status = result.destination.droppableId;
    await updateStatus(result.draggableId, status);
  };

  const markDayComplete = async () => {
    if (!user?.uid || !currentDay?.dateKey) return;
    await updateDoc(doc(db, "product_roadmaps", user.uid), {
      [`progress.completedDays.${currentDay.dateKey}`]: true,
    });
    navigate("/my-product-roadmap");
  };

  if (loading) {
    return (
      <Container>
        <BoardSurface>
          <Header>
            <HeaderLeft>
              <SkeletonPill />
              <SkeletonTitle />
              <SkeletonText $w="44%" />
              <SkeletonText $w="62%" />
            </HeaderLeft>
            <HeaderRight>
              <SkeletonChip />
              <SkeletonChip />
              <SkeletonButton />
              <SkeletonButton />
            </HeaderRight>
          </Header>

          <SummaryGrid>
            {Array.from({ length: 4 }).map((_, index) => (
              <SummaryCard key={index}>
                <SummaryIcon tone="cyan">
                  <SkeletonSquare />
                </SummaryIcon>
                <SummaryContent>
                  <SkeletonText $w="52%" />
                  <SkeletonValue />
                  <SkeletonText $w="86%" />
                </SummaryContent>
              </SummaryCard>
            ))}
          </SummaryGrid>

          <Board>
            {Array.from({ length: 3 }).map((_, columnIndex) => (
              <Column key={columnIndex}>
                <ColumnHeader status={STATUSES[columnIndex]}>
                  <HeaderDot status={STATUSES[columnIndex]} />
                  <HeaderText>{STATUSES[columnIndex].replace("_", " ")}</HeaderText>
                  <ColumnCount>0</ColumnCount>
                </ColumnHeader>
                {Array.from({ length: 3 }).map((__, cardIndex) => (
                  <Card key={cardIndex}>
                    <TaskTitle>
                      <TaskIndex>{cardIndex + 1}</TaskIndex>
                      <SkeletonText $w="74%" />
                    </TaskTitle>
                    <TaskHint>
                      <SkeletonText $w="92%" />
                      <SkeletonText $w="78%" />
                    </TaskHint>
                    <SkeletonSelect />
                  </Card>
                ))}
              </Column>
            ))}
          </Board>
        </BoardSurface>
      </Container>
    );
  }

  if (!roadmap || !currentDay) {
    return (
      <Page>
        <EmptyCard>
          <Sparkles size={18} />
          <h1>No product task board found</h1>
          <p>Open a valid product roadmap day first, then this board can guide today’s execution.</p>
          <ActionBtn onClick={() => navigate("/my-product-roadmap")}>Back to Product Roadmap</ActionBtn>
        </EmptyCard>
      </Page>
    );
  }

  if (!hasProAccess) {
    return (
      <Page>
        <EmptyCard>
          <Lock size={18} />
          <h1>Product task board is part of PRO</h1>
          <p>Upgrade to organize today’s product development work into clear TODO, in progress, and done lanes.</p>
          <ActionBtn onClick={() => navigate("/pricing")}>View Pricing</ActionBtn>
        </EmptyCard>
      </Page>
    );
  }

  return (
    <Container>
      <BoardSurface>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate("/my-product-roadmap")}>
              <ArrowLeft size={16} /> Back
            </BackButton>
            <HeroBadge>
              <Sparkles size={14} /> Product Task Board
            </HeroBadge>
            <h2>{currentDay.title}</h2>
            <HeaderMeta>
              <span>
                Day {currentDay.absoluteIndex + 1}
                {!isLocked && !isDayCompleted && hasProAccess && <StatusPill>ACTIVE</StatusPill>}
                {isLocked && <StatusPill locked>LOCKED</StatusPill>}
                {isDayCompleted && <StatusPill done>DONE</StatusPill>}
              </span>
              <span>{boardStatusLabel}</span>
            </HeaderMeta>
          </HeaderLeft>

          <HeaderRight>
            <MetaChip>
              <Target size={14} />
              {currentDay.weekFeatureFocus || currentDay.topic}
            </MetaChip>
            <MetaChip>
              <Clock3 size={14} />
              {currentDay.estimatedTime || "60-90 min"}
            </MetaChip>
            <SecondaryBtnLike onClick={() => navigate("/my-product-roadmap")}>
              Open Product Roadmap
            </SecondaryBtnLike>
            <PrimaryBtnLike
              disabled={isDayCompleted || !allTasksCompleted}
              onClick={markDayComplete}
            >
              {isDayCompleted
                ? "Day Completed"
                : allTasksCompleted
                ? "Mark Day Complete"
                : "Complete all tasks"}
            </PrimaryBtnLike>
          </HeaderRight>
        </Header>

        <SummaryGrid>
          <SummaryCard>
            <SummaryIcon tone="cyan">
              <Layers3 size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>Task Progress</SummaryLabel>
              <SummaryValue>{doneCount}/{tasks.length} done</SummaryValue>
              <SummaryHint>
                {completionPercent}% of today&apos;s product board is complete.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon tone="amber">
              <Target size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>In Motion</SummaryLabel>
              <SummaryValue>{inProgressCount} in progress</SummaryValue>
              <SummaryHint>
                {todoCount} task{todoCount !== 1 ? "s" : ""} still waiting to be started.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon tone="green">
              <Clock3 size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>Build Window</SummaryLabel>
              <SummaryValue>{currentDay.estimatedTime || "60-90 min"}</SummaryValue>
              <SummaryHint>
                Keep this as one focused build block instead of fragmented work.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon tone="violet">
              <Calendar size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>Completion State</SummaryLabel>
              <SummaryValue>
                {isDayCompleted ? "Day already completed" : "Complete after all tasks"}
              </SummaryValue>
              <SummaryHint>
                Mark the build day complete only after every task reaches done.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>
        </SummaryGrid>

        {isLocked && (
          <Banner locked>
            This task board is locked because this product build day is still upcoming. Stay on the current day first.
          </Banner>
        )}

        {isDayCompleted && (
          <Banner>
            This build day is already marked complete. You can still review the board, but task changes are locked to preserve progress.
          </Banner>
        )}

        {!tasks.length ? (
          <EmptyState>
            <CheckCircle2 size={18} />
            <div>
              <strong>No tasks were generated for this product day yet.</strong>
              <span>Open the roadmap day again once checklist items are available.</span>
            </div>
          </EmptyState>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Board>
              {STATUSES.map((status) => (
                <Droppable droppableId={status} key={status}>
                  {(provided) => (
                    <Column ref={provided.innerRef} {...provided.droppableProps}>
                      <ColumnHeader status={status}>
                        <HeaderDot status={status} />
                        <HeaderText>{status.replace("_", " ")}</HeaderText>
                        <ColumnCount>{grouped[status]?.length || 0}</ColumnCount>
                      </ColumnHeader>

                      {tasks
                        .filter((task) => task.status === status)
                        .map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(dragProvided) => (
                              <Card
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                <TaskTitle>
                                  <TaskIndex>{task.sequence}</TaskIndex>
                                  <strong>{task.title}</strong>
                                </TaskTitle>

                                <TaskHint>
                                  Move this task only when you actually start it, and mark it done after the output is finished.
                                </TaskHint>

                                <StatusSelect
                                  value={task.status}
                                  disabled={isInteractionLocked}
                                  onChange={(e) => updateStatus(task.id, e.target.value)}
                                >
                                  {STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                      {s.replace("_", " ")}
                                    </option>
                                  ))}
                                </StatusSelect>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </Column>
                  )}
                </Droppable>
              ))}
            </Board>
          </DragDropContext>
        )}
      </BoardSurface>
    </Container>
  );
}

const Container = styled.div`
  min-height: 100vh;
  padding: 118px 20px 80px;
  color: #fff;
  width: 100%;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;
const Page = Container;
const BoardSurface = styled.div`
  width: min(1160px, 100%);
  margin: 0 auto;
  padding: 28px;
  border-radius: 32px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.74)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 30%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);
`;
const EmptyCard = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 28px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.14);
  h1 { margin: 14px 0 0; color: #f8fafc; }
  p { margin: 12px 0 0; color: #94a3b8; line-height: 1.7; }
`;
const ActionBtn = styled.button`
  margin-top: 20px;
  padding: 12px 18px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
  font-weight: 900;
  cursor: pointer;
`;
const Board = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 980px){grid-template-columns:1fr;}
`;
const Column = styled.div`
  padding:16px; border-radius:24px; background:rgba(15,23,42,0.68); border:1px solid rgba(148,163,184,0.12);
`;
const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 12px;
  border-radius: 12px;
  background: ${({ status }) => {
    if (status === "TODO") return "rgba(99,102,241,0.12)";
    if (status === "IN_PROGRESS") return "rgba(245,158,11,0.12)";
    if (status === "DONE") return "rgba(34,197,94,0.12)";
    return "rgba(255,255,255,0.05)";
  }};
  border: 1px solid
    ${({ status }) => {
      if (status === "TODO") return "rgba(99,102,241,0.35)";
      if (status === "IN_PROGRESS") return "rgba(245,158,11,0.35)";
      if (status === "DONE") return "rgba(34,197,94,0.35)";
      return "#1f2937";
    }};
`;
const HeaderDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) => {
    if (status === "TODO") return "#6366f1";
    if (status === "IN_PROGRESS") return "#f59e0b";
    if (status === "DONE") return "#22c55e";
    return "#9ca3af";
  }};
  box-shadow: 0 0 0 3px
    ${({ status }) => {
      if (status === "TODO") return "rgba(99,102,241,0.25)";
      if (status === "IN_PROGRESS") return "rgba(245,158,11,0.25)";
      if (status === "DONE") return "rgba(34,197,94,0.25)";
      return "transparent";
    }};
`;
const HeaderText = styled.span`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #e5e7eb;
`;
const ColumnCount = styled.span`
  margin-left: auto;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;
const Card = styled.div`
  padding:14px; border-radius:18px; background:rgba(2,6,23,0.34); border:1px solid rgba(148,163,184,0.12);
`;
const TaskTitle = styled.div`
  display:flex; gap:8px; align-items:flex-start; margin-bottom:6px;
`;
const TaskIndex = styled.span`
  display:inline-flex; align-items:center; justify-content:center; min-width:22px; height:22px; font-size:12px; font-weight:700; color:#e0e7ff;
  background:linear-gradient(135deg, rgba(99, 102, 241, 0.9), rgba(139, 92, 246, 0.9));
  border-radius:999px; box-shadow:0 4px 12px rgba(99, 102, 241, 0.35), inset 0 0 0 1px rgba(255,255,255,0.15); flex-shrink:0;
`;
const TaskHint = styled.div`
  color:#94a3b8; font-size:13px; line-height:1.65;
`;
const StatusSelect = styled.select`
  width: 100%;
  margin-top: 14px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.7);
  color: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.16);
`;
const Header = styled.div`
  display:flex; justify-content:space-between; gap:20px; align-items:flex-start;
  @media (max-width: 920px){flex-direction:column;}
`;
const HeaderLeft = styled.div`
  min-width:0;
  h2 { margin:18px 0 0; color:#f8fafc; font-size:34px; line-height:1.08; }
`;
const HeaderRight = styled.div`
  display:flex; flex-wrap:wrap; gap:10px; justify-content:flex-end;
  @media (max-width: 920px){justify-content:flex-start;}
`;
const BackButton = styled.button`
  display:inline-flex; align-items:center; gap:8px; padding:10px 14px; border-radius:999px;
  border:1px solid rgba(148,163,184,0.14); background:rgba(15,23,42,0.52); color:#e2e8f0; cursor:pointer;
`;
const HeroBadge = styled.div`
  margin-top:14px;
  display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border-radius:999px;
  background:rgba(56,189,248,0.1); border:1px solid rgba(56,189,248,0.18); color:#7dd3fc; font-size:12px; font-weight:900;
`;
const HeaderMeta = styled.div`
  margin-top:12px; display:grid; gap:8px; color:#94a3b8; font-size:14px; line-height:1.6;
`;
const StatusPill = styled.span`
  margin-left:10px; padding:4px 10px; border-radius:999px;
  background:${({ locked, done }) => done ? "rgba(34, 197, 94, 0.14)" : locked ? "rgba(239, 68, 68, 0.14)" : "rgba(245, 158, 11, 0.14)"};
  color:${({ locked, done }) => done ? "#bbf7d0" : locked ? "#fecaca" : "#fde68a"};
  border:1px solid ${({ locked, done }) => done ? "rgba(34, 197, 94, 0.24)" : locked ? "rgba(239, 68, 68, 0.24)" : "rgba(245, 158, 11, 0.24)"};
  font-size:11px; font-weight:900; letter-spacing:0.04em; text-transform:uppercase;
`;
const MetaChip = styled.div`
  display:inline-flex; align-items:center; gap:8px; padding:10px 14px; border-radius:999px;
  background:rgba(15,23,42,0.58); border:1px solid rgba(148,163,184,0.14); color:#e2e8f0; font-size:12px; font-weight:800;
`;
const PrimaryBtnLike = styled.button`
  border:none; border-radius:999px; padding:12px 16px; background:linear-gradient(135deg,#f59e0b,#d97706); color:#111827; font-weight:900; cursor:pointer;
  &:disabled { opacity:0.45; cursor:not-allowed; }
`;
const SecondaryBtnLike = styled.button`
  border:1px solid rgba(148,163,184,0.16); border-radius:999px; padding:12px 16px; background:rgba(148,163,184,0.12); color:#e2e8f0; font-weight:900; cursor:pointer;
`;
const SummaryGrid = styled.div`
  margin-top:20px; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px;
  @media (max-width: 920px){grid-template-columns:repeat(2,minmax(0,1fr));}
  @media (max-width: 560px){grid-template-columns:1fr;}
`;
const SummaryCard = styled.div`
  display:flex; gap:14px; padding:18px; border-radius:22px; background:rgba(15,23,42,0.7); border:1px solid rgba(148,163,184,0.12);
`;
const SummaryIcon = styled.div`
  width:42px; height:42px; border-radius:14px; display:inline-flex; align-items:center; justify-content:center;
  background:${({ tone }) => tone === "amber" ? "rgba(245, 158, 11, 0.14)" : tone === "green" ? "rgba(34, 197, 94, 0.14)" : tone === "violet" ? "rgba(99, 102, 241, 0.14)" : "rgba(56, 189, 248, 0.14)"};
  color:${({ tone }) => tone === "amber" ? "#fbbf24" : tone === "green" ? "#86efac" : tone === "violet" ? "#c4b5fd" : "#7dd3fc"};
`;
const SummaryContent = styled.div`min-width:0;`;
const SummaryLabel = styled.div`
  color:#94a3b8; font-size:12px; font-weight:800;
`;
const SummaryValue = styled.div`
  margin-top:10px; color:#f8fafc; font-size:24px; line-height:1.1; font-weight:1000;
`;
const SummaryHint = styled.div`
  margin-top:8px; color:#64748b; font-size:12px; line-height:1.5;
`;
const Banner = styled.div`
  margin-top:18px; padding:14px 16px; border-radius:16px;
  background:${({ locked }) => locked ? "rgba(239, 68, 68, 0.12)" : "rgba(56, 189, 248, 0.08)"};
  border:1px solid ${({ locked }) => locked ? "rgba(239, 68, 68, 0.18)" : "rgba(56, 189, 248, 0.14)"};
  color:${({ locked }) => locked ? "#fecaca" : "#bae6fd"}; font-size:13px; line-height:1.7;
`;
const EmptyState = styled.div`
  margin-top:20px; display:flex; gap:12px; align-items:flex-start; padding:18px; border-radius:20px; background:rgba(15,23,42,0.7); border:1px solid rgba(148,163,184,0.12);
  strong { display:block; color:#f8fafc; }
  span { display:block; margin-top:6px; color:#94a3b8; line-height:1.6; }
`;

const shimmer = `
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.08) 0%,
    rgba(148, 163, 184, 0.18) 50%,
    rgba(148, 163, 184, 0.08) 100%
  );
  background-size: 200% 100%;
  animation: loadingShimmer 1.4s ease-in-out infinite;
`;

const SkeletonPill = styled.div`
  width: 150px;
  height: 30px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonTitle = styled.div`
  width: min(420px, 82%);
  height: 42px;
  margin-top: 18px;
  border-radius: 18px;
  ${shimmer}
`;

const SkeletonChip = styled.div`
  width: 140px;
  height: 42px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonButton = styled.div`
  width: 160px;
  height: 44px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonSquare = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 6px;
  ${shimmer}
`;

const SkeletonValue = styled.div`
  width: 96px;
  height: 28px;
  margin-top: 10px;
  border-radius: 12px;
  ${shimmer}
`;

const SkeletonSelect = styled.div`
  width: 100%;
  height: 40px;
  margin-top: 14px;
  border-radius: 10px;
  ${shimmer}
`;

const SkeletonText = styled.div`
  width: ${({ $w = "100%" }) => $w};
  height: 14px;
  margin-top: 12px;
  border-radius: 999px;
  ${shimmer}
`;
